import type { CategoryId, Listing } from "../types";
import { catalogRequest, matchesSearchQuery, type CatalogFilters } from "./catalog";
import { detectCategory } from "./category-detection";
import { emptySearchFilters } from "./search-navigation";
export type SearchSeed = { query: string; category: CategoryId | "all"; brand: string };
const bounded = (value: string, maximum: number) => [...value].filter(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127).join("").trim().slice(0, maximum);
export function rememberSearch(seeds: SearchSeed[], filters: CatalogFilters): SearchSeed[] {
  const query = bounded(filters.query, 120); const brand = bounded(filters.brand, 80);
  const category = filters.category !== "all" ? filters.category : detectCategory(query, brand) || "all";
  if (!query && !brand && category === "all") return seeds;
  const next: SearchSeed = { query, category, brand };
  const signature = JSON.stringify(next).toLowerCase();
  return [next, ...seeds.filter(seed => JSON.stringify(seed).toLowerCase() !== signature)].slice(0, 3);
}
// Functional, tab-memory-only inputs: no account IDs, timestamps, browser storage or profile history.
export function recommendationRequests(seeds: SearchSeed[]): string[] {
  const paths: string[] = [];
  for (const seed of seeds.slice(0, 2)) {
    if (seed.query.length >= 2) paths.push(catalogRequest({ ...emptySearchFilters(), query: seed.query }).path);
    if (seed.category !== "all" || seed.brand) paths.push(catalogRequest({ ...emptySearchFilters(), category: seed.category, brand: seed.brand }).path);
    if (seed.category !== "all" && seed.brand) paths.push(catalogRequest({ ...emptySearchFilters(), category: seed.category }).path);
  }
  paths.push(catalogRequest(emptySearchFilters()).path);
  return [...new Set(paths)];
}
export function rankRecommendations(listings: Listing[], seeds: SearchSeed[], limit = 24): Listing[] {
  const unique = [...new Map(listings.filter(listing => !listing.isDemo && /^[a-f0-9]{32}$/.test(listing.id)).map(listing => [listing.id, listing])).values()];
  const score = (listing: Listing) => seeds.reduce((total, seed, index) => {
    const recency = seeds.length - index;
    return total + recency * ((seed.query && matchesSearchQuery(listing, seed.query) ? 100 : 0) + (seed.category !== "all" && seed.category === listing.category ? 40 : 0) + (seed.brand && (listing.brand || "").toLowerCase().startsWith(seed.brand.toLowerCase()) ? 60 : 0));
  }, 0);
  return unique.map((listing, index) => ({ listing, index, score: score(listing) })).sort((a, b) => b.score - a.score || a.index - b.index).slice(0, Math.max(0, Math.min(24, limit))).map(entry => entry.listing);
}
